import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { campaignsApi } from "../../api/campaigns";
import axios from "axios";
import { StatusBadge } from "../../components/StatusBadge";
import { CampaignStatusBadge } from "../../components/CampaignStatusBadge";
import { CampaignStatus, CampaignStopResponse, ResidentInput, ResidentResponse } from "../../types";

export const CampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showLinks, setShowLinks] = useState(false);
  const [selectedResident, setSelectedResident] = useState<ResidentResponse | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editBuildingAddress, setEditBuildingAddress] = useState("");
  const [editBuildingId, setEditBuildingId] = useState("");
  const [, setEditResidents] = useState<ResidentInput[]>([]);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const { data: campaign, isLoading: loadingCampaign } = useQuery({
    queryKey: ["campaign", id],
    queryFn: () => campaignsApi.getCampaign(Number(id)),
    enabled: !!id,
  });

  const { data: responses, isLoading: loadingResponses } = useQuery({
    queryKey: ["campaign-responses", id],
    queryFn: () => campaignsApi.getCampaignResponses(Number(id)),
    enabled: !!id,
  });

  const { data: links } = useQuery({
    queryKey: ["campaign-links", id],
    queryFn: () => campaignsApi.getCampaignLinks(Number(id)),
    enabled: !!id && showLinks,
  });

  const updateMutation = useMutation({
    mutationFn: (data: {
      building_address?: string;
      building_id?: string;
      residents?: ResidentInput[];
    }) => campaignsApi.updateCampaign(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
      queryClient.invalidateQueries({ queryKey: ["campaign-responses", id] });
      queryClient.invalidateQueries({ queryKey: ["campaign-links", id] });
      setIsEditing(false);
      setMessage({ type: "success", text: "Campaign updated successfully!" });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (error: unknown) => {
      let errorMessage = "Failed to update campaign";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.detail || errorMessage;
      }
      setMessage({
        type: "error",
        text: errorMessage,
      });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const startMutation = useMutation({
    mutationFn: () => campaignsApi.startCampaign(Number(id)),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
      setMessage({
        type: "success",
        text: `Campaign started! ${data.emails_sent} email(s) sent.`,
      });
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (error: unknown) => {
      let errorMessage = "Failed to start campaign";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.detail || errorMessage;
      }
      setMessage({
        type: "error",
        text: errorMessage,
      });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => campaignsApi.stopCampaign(Number(id)),
    onSuccess: (data: CampaignStopResponse) => {
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
      setMessage({
        type: "success",
        text: data.message || "Campaign stopped successfully.",
      });
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (error: unknown) => {
      let errorMessage = "Failed to stop campaign";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.detail || errorMessage;
      }
      setMessage({
        type: "error",
        text: errorMessage,
      });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const handleEdit = () => {
    if (campaign) {
      setEditBuildingAddress(campaign.building_address);
      setEditBuildingId(campaign.building_id);
      // Get residents from links or responses
      if (links && links.length > 0) {
        // We need to get the full resident data, but for now we'll use a simplified approach
        // In a real scenario, you might want to fetch residents separately
        setEditResidents([]);
      }
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditBuildingAddress("");
    setEditBuildingId("");
    setEditResidents([]);
  };

  const handleSaveEdit = () => {
    const updateData: {
      building_address?: string;
      building_id?: string;
      residents?: ResidentInput[];
    } = {};

    if (editBuildingAddress !== campaign?.building_address) {
      updateData.building_address = editBuildingAddress;
    }
    if (editBuildingId !== campaign?.building_id) {
      updateData.building_id = editBuildingId;
    }

    // Note: For now, we're not handling resident updates in the edit form
    // This would require fetching the full resident list and allowing edits
    // For simplicity, we'll only update building info

    updateMutation.mutate(updateData);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert("Link copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-primary">Naef</h1>
              <span className="ml-4 text-gray-600">Campaign Detail</span>
            </div>
            <button onClick={() => navigate("/admin/dashboard")} className="btn btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message && (
          <div
            className={`mb-4 px-4 py-3 rounded ${
              message.type === "success"
                ? "bg-green-100 border border-green-400 text-green-700"
                : "bg-red-100 border border-red-400 text-red-700"
            }`}
          >
            {message.text}
          </div>
        )}

        {loadingCampaign ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">Loading campaign...</p>
          </div>
        ) : campaign ? (
          <>
            {/* Campaign Info Card */}
            <div className="card mb-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Campaign Information</h2>
                  {isEditing ? (
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Building Address
                        </label>
                        <input
                          type="text"
                          value={editBuildingAddress}
                          onChange={(e) => setEditBuildingAddress(e.target.value)}
                          className="input w-full"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Building ID
                        </label>
                        <input
                          type="text"
                          value={editBuildingId}
                          onChange={(e) => setEditBuildingId(e.target.value)}
                          className="input w-full"
                          required
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={handleSaveEdit}
                          disabled={updateMutation.isPending}
                          className="btn btn-primary"
                        >
                          {updateMutation.isPending ? "Saving..." : "Save Changes"}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={updateMutation.isPending}
                          className="btn btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-gray-600">Building Address: </span>
                        <span className="font-medium">{campaign.building_address}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Building ID: </span>
                        <span className="font-medium">{campaign.building_id}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Status: </span>
                        <CampaignStatusBadge status={campaign.status} />
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Created: </span>
                        <span className="font-medium">
                          {new Date(campaign.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                {!isEditing && (
                  <div className="flex gap-3">
                    {campaign.status === CampaignStatus.DRAFT && (
                      <>
                        <button onClick={handleEdit} className="btn btn-secondary">
                          Edit Campaign
                        </button>
                        <button
                          onClick={() => startMutation.mutate()}
                          disabled={startMutation.isPending}
                          className="btn btn-primary"
                        >
                          {startMutation.isPending ? "Starting..." : "Start Campaign"}
                        </button>
                      </>
                    )}
                    {campaign.status === CampaignStatus.ACTIVE && (
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              "Are you sure you want to stop this campaign? No further emails will be sent."
                            )
                          ) {
                            stopMutation.mutate();
                          }
                        }}
                        disabled={stopMutation.isPending}
                        className="btn bg-orange-600 hover:bg-orange-700 text-white"
                      >
                        {stopMutation.isPending ? "Stopping..." : "Stop Campaign"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-6">
              <button onClick={() => setShowLinks(!showLinks)} className="btn btn-primary">
                {showLinks ? "Hide Links" : "Show Resident Links"}
              </button>
            </div>
          </>
        ) : null}

        {showLinks && links && (
          <div className="card mb-8">
            <h2 className="text-xl font-bold mb-4">Resident Links</h2>
            <div className="space-y-2">
              {links.map((link) => (
                <div
                  key={link.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded"
                >
                  <div>
                    <p className="font-medium">{link.name}</p>
                    <p className="text-sm text-gray-600">{link.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={link.status} />
                    <button
                      onClick={() => copyToClipboard(link.link)}
                      className="btn btn-secondary text-sm"
                    >
                      Copy Link
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card">
          <h2 className="text-xl font-bold mb-4">Resident Responses</h2>

          {loadingResponses ? (
            <p className="text-center py-8">Loading...</p>
          ) : responses && responses.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reminders
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {responses.map((resident) => (
                    <tr key={resident.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{resident.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{resident.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={resident.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{resident.reminder_count}</div>
                        {resident.last_sent_at && (
                          <div className="text-xs text-gray-500">
                            Last: {new Date(resident.last_sent_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {resident.response ? (
                          <button
                            onClick={() => setSelectedResident(resident)}
                            className="text-primary hover:text-primary-dark text-sm font-medium"
                          >
                            View Response
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm">No response</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center py-8 text-gray-500">No residents in this campaign</p>
          )}
        </div>

        {/* Response Modal */}
        {selectedResident && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold">Response Details</h3>
                <button
                  onClick={() => setSelectedResident(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="font-medium text-gray-700">Resident</p>
                  <p className="text-gray-900">{selectedResident.name}</p>
                </div>

                {selectedResident.response && (
                  <>
                    {/* Individual Tenants */}
                    {selectedResident.response.particulier_tenants.length > 0 && (
                      <div>
                        <p className="font-medium text-gray-700 mb-2">Individual Tenants</p>
                        <div className="bg-gray-50 p-4 rounded space-y-3">
                          {selectedResident.response.particulier_tenants.map((tenant, idx) => (
                            <div key={idx} className="border-b border-gray-200 pb-3 last:border-0">
                              <p className="font-semibold text-sm mb-2">Tenant {idx + 1}</p>
                              <pre className="text-sm whitespace-pre-wrap">
                                {JSON.stringify(tenant, null, 2)}
                              </pre>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Enterprise Tenants */}
                    {selectedResident.response.enterprise_tenants.length > 0 && (
                      <div>
                        <p className="font-medium text-gray-700 mb-2">Enterprise Tenants</p>
                        <div className="bg-gray-50 p-4 rounded space-y-3">
                          {selectedResident.response.enterprise_tenants.map((tenant, idx) => (
                            <div key={idx} className="border-b border-gray-200 pb-3 last:border-0">
                              <p className="font-semibold text-sm mb-2">Company {idx + 1}</p>
                              <pre className="text-sm whitespace-pre-wrap">
                                {JSON.stringify(tenant, null, 2)}
                              </pre>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Shared Fields */}
                    <div>
                      <p className="font-medium text-gray-700 mb-2">Shared Information</p>
                      <div className="bg-gray-50 p-4 rounded">
                        <pre className="text-sm whitespace-pre-wrap">
                          {JSON.stringify(
                            {
                              cave: selectedResident.response.cave,
                              parking: selectedResident.response.parking,
                              grenier: selectedResident.response.grenier,
                              nom_banque: selectedResident.response.nom_banque,
                              titulaire_compte: selectedResident.response.titulaire_compte,
                              iban: selectedResident.response.iban,
                              ...(campaign?.is_ppe
                                ? {
                                    clearing: selectedResident.response.clearing,
                                    numero_appartement:
                                      selectedResident.response.numero_appartement,
                                    autre: selectedResident.response.autre,
                                    locataire_nom: selectedResident.response.locataire_nom,
                                    locataire_prenom: selectedResident.response.locataire_prenom,
                                    locataire_tel_prive:
                                      selectedResident.response.locataire_tel_prive,
                                    locataire_tel_portable:
                                      selectedResident.response.locataire_tel_portable,
                                    locataire_gerance: selectedResident.response.locataire_gerance,
                                    moyen_paiement: selectedResident.response.moyen_paiement,
                                  }
                                : {
                                    nom_assurance_rc: selectedResident.response.nom_assurance_rc,
                                    numero_police_rc: selectedResident.response.numero_police_rc,
                                  }),
                              comment: selectedResident.response.comment,
                            },
                            null,
                            2
                          )}
                        </pre>
                      </div>
                    </div>

                    {/* Files */}
                    <div>
                      <p className="font-medium text-gray-700 mb-2">Files</p>
                      <div className="bg-gray-50 p-4 rounded">
                        {selectedResident.response.file_urls.length > 0 ? (
                          <ul className="list-disc list-inside space-y-2">
                            {selectedResident.response.file_urls.map((file, idx) => {
                              const getFileTypeLabel = (
                                fileType: string | null | undefined
                              ): string => {
                                switch (fileType) {
                                  case "id_passport":
                                    return "ID/Passport";
                                  case "wedding_divorce":
                                    return "Marriage Certificate";
                                  case "police_rc":
                                    return "Insurance RC Policy";
                                  case "lsv":
                                    return "LSV/DD Form";
                                  default:
                                    return "Unknown File Type";
                                }
                              };

                              return (
                                <li key={idx} className="text-sm">
                                  <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline"
                                  >
                                    {file.original_filename || getFileTypeLabel(file.file_type)}
                                  </a>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-500">No files uploaded</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <p className="font-medium text-gray-700">Last Updated</p>
                      <p className="text-gray-900">
                        {new Date(selectedResident.response.updated_at).toLocaleString()}
                      </p>
                    </div>
                  </>
                )}
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setSelectedResident(null)}
                  className="btn btn-secondary w-full"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
